import React, {useMemo} from 'react';
import {Body, Button, designSystemToken} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {
  getSortLabels,
  getStatusLabels,
} from '@/views/creators/recruit-crm/constants';
import {
  RECRUITMENT_FILTER_STATUSES,
  RECRUITMENT_SORT_BY_OPTIONS,
  RecruitmentFiltersState,
  RecruitmentFilterStatus,
  RecruitmentSortBy,
  RecruitmentSortingState,
} from '@/views/creators/recruit-crm/types';
import DropdownButton, {
  Option,
} from '@/components/dropdown-button/DropdownButton';
import Dropdown, {DropdownOption} from '@/components/dropdown/Dropdown';
import styles from './RecruitCrmToolbar.module.scss';

interface RecruitCrmToolbarProps {
  totalResults: number;
  filters: RecruitmentFiltersState;
  sorting: RecruitmentSortingState;
  onFilterChange: (filters: Partial<RecruitmentFiltersState>) => void;
  onSortChange: (sortBy: RecruitmentSortBy) => void;
  onRecruitByEmail: () => void;
}

function RecruitCrmToolbar({
  totalResults,
  filters,
  sorting,
  onFilterChange,
  onSortChange,
  onRecruitByEmail,
}: RecruitCrmToolbarProps) {
  const statusLabels = useMemo(() => getStatusLabels(), []);
  const sortLabels = useMemo(() => getSortLabels(), []);

  const statusOptions = useMemo<DropdownOption[]>(
    () =>
      RECRUITMENT_FILTER_STATUSES.map((status) => ({
        id: status,
        label: statusLabels[status],
      })),
    [statusLabels]
  );

  const sortOptions = useMemo<Array<Option<string>>>(
    () =>
      RECRUITMENT_SORT_BY_OPTIONS.map((sortBy) => ({
        id: sortBy,
        label: sortLabels[sortBy],
      })),
    [sortLabels]
  );

  const selectedStatusIds = useMemo(
    () => filters.status ?? [...RECRUITMENT_FILTER_STATUSES],
    [filters.status]
  );

  // Get current status filter label
  const statusLabel = useMemo(() => {
    if (
      selectedStatusIds.length === 0 ||
      selectedStatusIds.length === RECRUITMENT_FILTER_STATUSES.length
    ) {
      return translate('All Statuses');
    }
    if (selectedStatusIds.length === 1) {
      const opt = statusOptions.find(
        (option) => option.id === selectedStatusIds[0]
      );
      return opt?.label ?? translate('Status');
    }
    return `${selectedStatusIds.length} ${translate('Statuses')}`;
  }, [selectedStatusIds, statusOptions]);

  // Get current sort label
  const sortLabel = useMemo(() => {
    const sortOption = sortOptions.find((opt) => opt.id === sorting.sortBy);
    return sortOption
      ? `${translate('Sort by')}: ${sortOption.label}`
      : translate('Sort by');
  }, [sorting.sortBy, sortOptions]);

  // Toggle a status in the multi-select
  const handleStatusToggle = (option: DropdownOption) => {
    const isSelected = selectedStatusIds.includes(
      option.id as RecruitmentFilterStatus
    );
    let newSelected: RecruitmentFilterStatus[];

    if (isSelected) {
      newSelected = selectedStatusIds.filter((status) => status !== option.id);
    } else {
      newSelected = [
        ...selectedStatusIds,
        option.id as RecruitmentFilterStatus,
      ];
    }

    if (
      newSelected.length === 0 ||
      newSelected.length === RECRUITMENT_FILTER_STATUSES.length
    ) {
      onFilterChange({status: undefined});
    } else {
      onFilterChange({status: newSelected});
    }
  };

  // Handle sort change
  const handleSortChange = (option: Option<string>) => {
    onSortChange(option.id as RecruitmentSortBy);
  };

  return (
    <div className={styles.toolbar}>
      <div className={styles.leftSection}>
        {/* Status Filter */}
        <div className={styles.filterGroup}>
          <Body
            size="sm"
            color={designSystemToken('semantic.fg.secondary')}
            className={styles.statusLabel}
          >
            {translate('Status')}:
          </Body>
          <Dropdown
            label={statusLabel}
            options={statusOptions}
            selectedIds={selectedStatusIds}
            onChange={handleStatusToggle}
            selectionMode="multiCheckboxes"
            menuWidth={240}
            showItemDividers={false}
            menuPositionHorizontal="left"
            disableSelectedBackground
          />
        </div>
      </div>

      <div className={styles.rightSection}>
        {/* Total Results */}
        <Body size="lg" color={designSystemToken('semantic.fg.secondary')}>
          {translate('recruitments', {
            count: totalResults,
          })}
        </Body>

        {/* Add by Email */}
        <Button
          mode="tinted"
          appearance="neutral"
          size="small"
          onClick={onRecruitByEmail}
        >
          {translate('Add by Email')}
        </Button>

        {/* Sort Dropdown */}
        <DropdownButton
          label={sortLabel}
          options={sortOptions}
          onChange={handleSortChange}
          appearance="neutral"
          mode="plain"
          size="small"
        />
      </div>
    </div>
  );
}

export default RecruitCrmToolbar;
